// Utility helpers for CSV upload hook
import type {
  CsvFileConfig,
  CsvFileUploadState,
  LookupTabData,
  TabViewMode,
} from '~/app/components/knowledgeBase/componentSchemaManager/ComponentSchemaManager.types';

export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export function parseCsvText(text: string): {
  headers: string[];
  rows: string[][];
} {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length === 0) return { headers: [], rows: [] };

  const headers = lines[0].split(',').map((h) => h.trim());
  const rows = lines
    .slice(1)
    .map((line) => line.split(',').map((v) => v.trim()));

  return { headers, rows };
}

export function validateCsv(
  config: CsvFileConfig,
  headers: string[],
  rows: string[][],
): string[] {
  const errors: string[] = [];
  const expectedKeys = config.columns
    .filter((c) => !c.excludeFromCsv)
    .map((c) => c.key);
  const requiredKeys = config.columns
    .filter((c) => c.required && !c.excludeFromCsv)
    .map((c) => c.key);

  for (const key of requiredKeys) {
    if (!headers.includes(key)) {
      errors.push(`Missing required column: "${key}"`);
    }
  }

  for (const header of headers) {
    if (!expectedKeys.includes(header)) {
      errors.push(`Unexpected column: "${header}"`);
    }
  }

  if (rows.length === 0) {
    errors.push('CSV file contains no data rows');
  }

  const headerCount = headers.length;
  rows.forEach((row, i) => {
    if (row.length !== headerCount) {
      errors.push(
        `Row ${i + 1}: expected ${headerCount} columns, got ${row.length}`,
      );
    }
  });

  const requiredIndices = requiredKeys
    .map((key) => headers.indexOf(key))
    .filter((idx) => idx >= 0);

  rows.forEach((row, i) => {
    for (const idx of requiredIndices) {
      if (!row[idx] || row[idx].trim() === '') {
        errors.push(`Row ${i + 1}: required field "${headers[idx]}" is empty`);
      }
    }
  });

  if (errors.length > 20) {
    const total = errors.length;
    errors.length = 20;
    errors.push(`... and ${total - 20} more errors`);
  }

  return errors;
}

let _keyCounter = 0;
export const nextKey = () => `row_${++_keyCounter}_${Date.now()}`;

export const createInitialUploads = (
  configs: CsvFileConfig[],
): Record<string, CsvFileUploadState> => {
  const uploads: Record<string, CsvFileUploadState> = {};
  for (const config of configs) {
    uploads[config.name] = {
      file: null,
      status: 'idle',
      data: [],
      errors: [],
      fileName: config.fileName,
    };
  }
  return uploads;
};

export const createInitialLookups = (
  configs: CsvFileConfig[],
): Record<string, LookupTabData> => {
  const lookups: Record<string, LookupTabData> = {};
  for (const config of configs) {
    lookups[config.name] = { rows: [], loading: false, error: null };
  }
  return lookups;
};

export const createInitialViewModes = (
  configs: CsvFileConfig[],
): Record<string, TabViewMode> => {
  return configs.reduce<Record<string, TabViewMode>>((modes, config) => {
    modes[config.name] = 'crud';
    return modes;
  }, {});
};
